import {
  Download,
  LayoutDashboard,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Redo2,
  Sun,
  Undo2,
  Upload,
} from "lucide-react";
import type { KeyboardEvent } from "react";

export type ThemeMode = "light" | "dark";
export type SaveStatus = "saved" | "saving" | "dirty" | "error";

export interface TitlebarProps {
  projectTitle: string;
  onProjectTitleChange: (title: string) => void;
  onProjectTitleCommit?: (title: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  onImport: () => void;
  onExport: () => void;
  theme: ThemeMode;
  onToggleTheme: () => void;
  leftSidebarVisible: boolean;
  rightSidebarVisible: boolean;
  onToggleLeftSidebar: () => void;
  onToggleRightSidebar: () => void;
  saveStatus?: SaveStatus;
  disabled?: boolean;
}

const saveStatusCopy: Record<SaveStatus, string> = {
  saved: "已保存到本地",
  saving: "正在保存…",
  dirty: "等待保存",
  error: "保存失败",
};

const saveStatusColor: Record<SaveStatus, string> = {
  saved: "var(--success)",
  saving: "var(--accent)",
  dirty: "#d49a24",
  error: "var(--danger)",
};

export function Titlebar({
  projectTitle,
  onProjectTitleChange,
  onProjectTitleCommit,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onImport,
  onExport,
  theme,
  onToggleTheme,
  leftSidebarVisible,
  rightSidebarVisible,
  onToggleLeftSidebar,
  onToggleRightSidebar,
  saveStatus = "saved",
  disabled = false,
}: TitlebarProps) {
  const commitTitle = () => onProjectTitleCommit?.(projectTitle.trim());

  const handleTitleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };

  const togglesToDark = theme === "light";

  return (
    <header className="titlebar" aria-label="项目工具栏">
      <div className="titlebar__left">
        <div className="titlebar-brand" aria-label="Kimnote">
          <span className="brand-mark titlebar-brand__mark" aria-hidden="true">
            <LayoutDashboard size={15} strokeWidth={2.2} />
          </span>
          <strong>Kimnote</strong>
        </div>
        <button
          type="button"
          className="titlebar-icon-button titlebar-panel-toggle titlebar-panel-toggle--left"
          onClick={onToggleLeftSidebar}
          disabled={disabled}
          aria-label={leftSidebarVisible ? "收起左侧栏" : "展开左侧栏"}
          aria-expanded={leftSidebarVisible}
          aria-controls="project-sidebar"
          title={leftSidebarVisible ? "收起项目栏" : "展开项目栏"}
        >
          {leftSidebarVisible ? <PanelLeftClose size={16} aria-hidden="true" /> : <PanelLeftOpen size={16} aria-hidden="true" />}
        </button>
        <span className="titlebar-divider" aria-hidden="true" />
        <div className="titlebar__actions" role="group" aria-label="历史记录">
          <button
            type="button"
            className="titlebar-icon-button"
            onClick={onUndo}
            disabled={disabled || !canUndo}
            aria-label="撤销"
            title="撤销 (⌘Z)"
          >
            <Undo2 size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="titlebar-icon-button"
            onClick={onRedo}
            disabled={disabled || !canRedo}
            aria-label="重做"
            title="重做 (⇧⌘Z)"
          >
            <Redo2 size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="titlebar__center">
        <input
          className="project-title-input"
          value={projectTitle}
          onChange={(event) => onProjectTitleChange(event.currentTarget.value)}
          onBlur={commitTitle}
          onKeyDown={handleTitleKeyDown}
          aria-label="项目名称"
          placeholder="未命名项目"
          maxLength={160}
          disabled={disabled}
          spellCheck={false}
        />
      </div>

      <div className="titlebar__actions">
        <button
          type="button"
          className="titlebar-icon-button titlebar-panel-toggle titlebar-panel-toggle--right"
          onClick={onToggleRightSidebar}
          disabled={disabled}
          aria-label={rightSidebarVisible ? "收起右侧栏" : "展开右侧栏"}
          aria-expanded={rightSidebarVisible}
          aria-controls="canvas-inspector"
          title={rightSidebarVisible ? "收起检查器" : "展开检查器"}
        >
          {rightSidebarVisible ? <PanelRightClose size={16} aria-hidden="true" /> : <PanelRightOpen size={16} aria-hidden="true" />}
        </button>
        <span className="titlebar-divider" aria-hidden="true" />
        <button
          type="button"
          className="titlebar-icon-button"
          onClick={onImport}
          disabled={disabled}
          aria-label="导入项目包"
          title="导入项目包"
        >
          <Upload size={16} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="titlebar-icon-button"
          onClick={onExport}
          disabled={disabled}
          aria-label="导出项目包"
          title="导出项目包"
        >
          <Download size={16} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="titlebar-icon-button"
          onClick={onToggleTheme}
          disabled={disabled}
          aria-label={togglesToDark ? "切换到深色主题" : "切换到浅色主题"}
          aria-pressed={theme === "dark"}
          title={togglesToDark ? "深色主题" : "浅色主题"}
        >
          {togglesToDark ? (
            <Moon size={16} aria-hidden="true" />
          ) : (
            <Sun size={16} aria-hidden="true" />
          )}
        </button>

        <div className="save-state" role="status" aria-live="polite">
          <span
            className="save-state__dot"
            style={{
              background: saveStatusColor[saveStatus],
              boxShadow: `0 0 0 3px color-mix(in srgb, ${saveStatusColor[saveStatus]} 13%, transparent)`,
            }}
          />
          <span>{saveStatusCopy[saveStatus]}</span>
        </div>
      </div>
    </header>
  );
}
